import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Lock, User, Eye, EyeOff } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function LoginPage() {
  const [form, setForm] = useState({ usuario: "", contrasena: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/login`, form);
      const { token, usuario } = res.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(usuario));

      try {
        const { data: permisosList } = await axios.get(
          `${API_URL}/usuarios/${usuario.id}/permisos`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const permisosMap = {};
        permisosList.forEach((p) => { permisosMap[p.menu_key] = !!p.tiene_acceso; });
        localStorage.setItem("permisos", JSON.stringify(permisosMap));
      } catch {
        localStorage.removeItem("permisos");
      }

      navigate("/dashboard");
    } catch (err) {
      setLoading(false);
      setError(
        err.response?.data?.error ||
          "Error al iniciar sesión, intenta de nuevo",
      );
    }
  };

  return (
    <div className="min-h-screen flex overflow-hidden">
      {/* Panel izquierdo — decorativo */}
      <div className="hidden lg:flex lg:w-1/2 bg-hero-gradient flex-col items-center justify-center p-12 relative overflow-hidden">
        {/* Círculos decorativos */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-white/5 rounded-full" />
        <div className="absolute top-1/4 right-8 w-32 h-32 bg-white/10 rounded-full" />

        <div className="relative z-10 text-center">
          <img
            src="/5.png"
            alt="Vetronix"
            className="h-28 w-auto max-w-xs object-contain mx-auto mb-6 drop-shadow-lg"
          />
          <p className="text-purple-200 text-lg mb-8 max-w-xs leading-relaxed">
            Sistema de gestión de laboratorio clínico veterinario
          </p>

          <div className="grid grid-cols-2 gap-4 max-w-sm">
            {[
              { label: "Análisis clínicos", icon: "🔬" },
              { label: "Resultados digitales", icon: "📊" },
              { label: "Gestión de pacientes", icon: "🐾" },
              { label: "Facturación integrada", icon: "🧾" },
            ].map((f) => (
              <div
                key={f.label}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-left"
              >
                <span className="text-xl">{f.icon}</span>
                <p className="text-white text-xs font-medium mt-1">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 flex items-center justify-center bg-brand-50 px-6 py-12">
        <div className="w-full max-w-md animate-fadeIn">
          {/* Logo móvil */}
          <div className="lg:hidden flex justify-center mb-8">
            <img
              src="/vetronix.png"
              alt="Vetronix"
              className="h-16 w-auto max-w-[200px] object-contain"
            />
          </div>

          <div className="bg-white rounded-2xl shadow-card-hover border border-brand-100 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Bienvenido</h2>
              <p className="text-gray-500 text-sm mt-1">
                Inicia sesión para continuar
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Usuario
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-400" />
                  <input
                    type="text"
                    name="usuario"
                    value={form.usuario}
                    onChange={handleChange}
                    placeholder="Ingresa tu usuario"
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-brand-50 border border-brand-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-400" />
                  <input
                    type={showPass ? "text" : "password"}
                    name="contrasena"
                    value={form.contrasena}
                    onChange={handleChange}
                    placeholder="Ingresa tu contraseña"
                    required
                    className="w-full pl-10 pr-10 py-2.5 bg-brand-50 border border-brand-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-500 transition-colors"
                  >
                    {showPass ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-brand-gradient text-white font-semibold rounded-xl transition-all duration-200 hover:opacity-90 hover:shadow-md active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none mt-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Validando...
                  </span>
                ) : (
                  "Ingresar"
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            © {new Date().getFullYear()} Vetronix — Sistema de laboratorio
            veterinario
          </p>
        </div>
      </div>
    </div>
  );
}
